import React, { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import { i18n } from "../../translate/i18n";

const PROBLEM_REASONS = ["wrongInfo", "misunderstood", "lateTransfer", "needlessTransfer", "tone", "other"];

const useStyles = makeStyles()((theme) => ({
  banner: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    padding: theme.spacing(0.75, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
    flexShrink: 0,
    flexWrap: "wrap",
  },
  icon: {
    color: theme.palette.primary.main,
    fontSize: 20,
  },
  text: {
    fontSize: 13,
    flex: 1,
    minWidth: 200,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },
  reasons: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    margin: theme.spacing(1, 0, 2),
  },
}));

const AiReviewBanner = ({ ticket }) => {
  const { classes } = useStyles();
  const [pending, setPending] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const ticketId = ticket?.id;
  const isOpen = ticket?.status === "open";

  useEffect(() => {
    setPending(false);
    setDismissed(false);
    if (!ticketId || !isOpen) return undefined;

    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/ai-replies/${ticketId}/agent-review`);
        if (alive) setPending(Boolean(data?.pending));
      } catch {
      }
    })();

    return () => {
      alive = false;
    };
  }, [ticketId, isOpen]);

  const submit = async (status, text) => {
    setSaving(true);
    try {
      await api.put(`/ai-replies/${ticketId}/audit`, { status, comment: text });
      toast.success(i18n.t("aiReviewBanner.thanks"));
      setPending(false);
      setDialogOpen(false);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const submitProblem = () => {
    const label = i18n.t(`aiReviewBanner.reasons.${reason}`);
    const extra = comment.trim();
    submit("problem", extra ? `${label} — ${extra}` : label);
  };

  const openProblemDialog = () => {
    setReason("");
    setComment("");
    setDialogOpen(true);
  };

  if (!pending || dismissed || !isOpen) return null;

  return (
    <>
      <Box className={classes.banner}>
        <SmartToyOutlinedIcon className={classes.icon} />
        <Typography className={classes.text}>{i18n.t("aiReviewBanner.question")}</Typography>
        <div className={classes.actions}>
          <Button
            size="small"
            variant="outlined"
            color="success"
            startIcon={<ThumbUpOutlinedIcon fontSize="small" />}
            disabled={saving}
            onClick={() => submit("reviewed", null)}
          >
            {i18n.t("aiReviewBanner.good")}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<ThumbDownOutlinedIcon fontSize="small" />}
            disabled={saving}
            onClick={openProblemDialog}
          >
            {i18n.t("aiReviewBanner.problem")}
          </Button>
          <Tooltip title={i18n.t("aiReviewBanner.later")}>
            <IconButton size="small" onClick={() => setDismissed(true)}>
              <CloseOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{i18n.t("aiReviewBanner.dialogTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            {i18n.t("aiReviewBanner.dialogHelp")}
          </Typography>
          <div className={classes.reasons}>
            {PROBLEM_REASONS.map((key) => (
              <Chip
                key={key}
                label={i18n.t(`aiReviewBanner.reasons.${key}`)}
                color={reason === key ? "error" : "default"}
                variant={reason === key ? "filled" : "outlined"}
                onClick={() => setReason(key)}
              />
            ))}
          </div>
          <TextField
            label={i18n.t("aiReviewBanner.comment")}
            placeholder={i18n.t("aiReviewBanner.commentPlaceholder")}
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{i18n.t("aiReviewBanner.cancel")}</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!reason || (reason === "other" && !comment.trim()) || saving}
            onClick={submitProblem}
          >
            {i18n.t("aiReviewBanner.send")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AiReviewBanner;
